/**
 * Reusable product parts of assistant views (the quote preview, later the
 * product selection): the thumbnail, the display-data hook with its one-time
 * refetch of an expired signed image URL, and the lazily loaded product
 * details modal.
 */
import { useApolloClient } from "@apollo/client";
import { Box, Stack, Typography } from "@mui/material";
import { useFormatting } from "@msys/ui";
import { useTranslate } from "@tolgee/react";
import React from "react";
import {
  fetchProductDisplay,
  productId,
  type ProductDisplay,
  type ProductIdentity,
  type ProductPick,
} from "./productDisplay.js";

export function ProductThumb({
  imageUrl,
  onError,
}: {
  imageUrl: string | null | undefined;
  onError: () => void;
}) {
  return (
    <Box
      sx={{
        width: 48,
        height: 48,
        flexShrink: 0,
        borderRadius: 1,
        bgcolor: "grey.100",
        overflow: "hidden",
      }}
    >
      {imageUrl && (
        <Box
          component="img"
          src={imageUrl}
          alt=""
          onError={onError}
          sx={{ width: 1, height: 1, objectFit: "contain" }}
        />
      )}
    </Box>
  );
}

/**
 * The product's display data. A product that arrives without any (a
 * taken-over selection reloaded from the chat; `lookupMissing`) is looked up
 * once by article number; an image that fails to load (the signed URL
 * expires) is refetched once before the placeholder takes over.
 */
export function useProductDisplayData(
  product: ProductPick,
  lookupMissing: boolean
): {
  imageUrl: string | null | undefined;
  brand: string | null | undefined;
  price: ProductPick["price"];
  onImageError: () => void;
} {
  const client = useApolloClient();
  const [looked, setLooked] = React.useState<ProductDisplay | null>(null);
  const [imageUrlOverride, setImageUrlOverride] = React.useState<
    string | null | undefined
  >(undefined);
  const retried = React.useRef(false);
  const id = productId(product);
  // Only a product that came without display fields at all (undefined, not
  // null = "none") is looked up.
  const missing =
    lookupMissing &&
    product.imageUrl === undefined &&
    product.brand === undefined &&
    product.price === undefined;

  React.useEffect(() => {
    if (!missing) return;
    let cancelled = false;
    void fetchProductDisplay(client, product).then(result => {
      if (!cancelled) setLooked(result);
    });
    return () => {
      cancelled = true;
    };
    // `product` is identified by `id`; its other fields do not change the lookup.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, id, missing]);

  const current =
    imageUrlOverride !== undefined
      ? imageUrlOverride
      : (looked?.imageUrl ?? product.imageUrl);

  const onImageError = () => {
    if (retried.current) {
      setImageUrlOverride(null);
      return;
    }
    retried.current = true;
    void fetchProductDisplay(client, product).then(result => {
      const fresh = result?.imageUrl ?? null;
      setImageUrlOverride(fresh !== current ? fresh : null);
    });
  };

  return {
    imageUrl: current,
    brand: product.brand ?? looked?.brand,
    price: product.price ?? looked?.price,
    onImageError,
  };
}

const ProductOverviewModal = React.lazy(() =>
  import("../../products/modals/ProductOverviewModal.js").then(module => ({
    default: module.ProductOverviewModal,
  }))
);

/** The catalogue's product details (the existing `ProductOverviewModal`), loaded on first open. */
export function ProductDetails({
  product,
  onClose,
}: {
  product: ProductIdentity;
  onClose: () => void;
}) {
  return (
    <React.Suspense fallback={null}>
      <ProductOverviewModal
        productArticleNumber={product.articleNumber}
        productSupplierId={product.supplierId}
        handleClose={onClose}
      />
    </React.Suspense>
  );
}

function ProductPrice({ price }: { price: ProductPick["price"] }) {
  const { getFormattedPrice } = useFormatting();
  if (!price) return null;
  return (
    <Typography variant="caption" color="text.secondary">
      {getFormattedPrice(price.amount)}
    </Typography>
  );
}

/**
 * A product row: thumbnail, title, brand · article number, price and the
 * assistant's reason. With `onOpen` the row is a button (e.g. to open the
 * product details).
 */
export function ProductSummary({
  product,
  reason,
  onOpen,
  lookupMissing = false,
}: {
  product: ProductPick;
  /** Looks the display data up in the catalogue when the product has none (taken-over products only). */
  lookupMissing?: boolean;
  reason?: string;
  onOpen?: () => void;
}) {
  const { t } = useTranslate("Assistant");
  const display = useProductDisplayData(product, lookupMissing);
  const content = (
    <Stack
      direction="row"
      spacing={1}
      sx={{ alignItems: "center", minWidth: 0, textAlign: "left" }}
    >
      <ProductThumb
        imageUrl={display.imageUrl}
        onError={display.onImageError}
      />
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {product.title}
        </Typography>
        <Typography variant="caption" color="text.secondary" component="div">
          {[display.brand, product.articleNumber].filter(Boolean).join(" · ")}
        </Typography>
        <ProductPrice price={display.price} />
        {reason && (
          <Typography variant="caption" component="div">
            {reason}
          </Typography>
        )}
      </Box>
    </Stack>
  );
  if (!onOpen) return content;
  return (
    <Box
      component="button"
      type="button"
      aria-label={t("Details for {title}", {
        title: product.title,
        ns: "Assistant",
      })}
      onClick={onOpen}
      sx={{ all: "unset", cursor: "pointer", flex: 1, minWidth: 0 }}
    >
      {content}
    </Box>
  );
}
